import type { DisplayAttendeeBookingsSchema } from '@/schemas/attendee.schema'
import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '../ui/badge'
import {
  formatDateTimeIntl,
  isUserSuperAdmin,
  paymentStatusClasses,
} from '@/utils/helper'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { Button } from '../ui/button'
import { Download, Loader } from 'lucide-react'

export interface EventAttendeeDetails {
  id: string
  name: string
  consentFormUrl: string
}

export const getEventAttendeeColumns = (
  onDownloadConsentFormClick: (
    downloadingAttendeeDetails: EventAttendeeDetails
  ) => void,
  attendeeId: string | null | undefined
): ColumnDef<DisplayAttendeeBookingsSchema>[] => {
  const isSuperAdmin = isUserSuperAdmin()

  const baseColumns: ColumnDef<DisplayAttendeeBookingsSchema>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return (
          <span className="font-semibold text-sm text-ss-neutral-950 leading-4.5  wrap-anywhere w-32">
            {row?.original?.name}
          </span>
        )
      },
    },
    {
      accessorKey: 'eventNName',
      header: 'Event Name',
      cell: ({ row }) => {
        return (
          <span className="font-semibold text-sm text-ss-neutral-950 leading-4.5  wrap-anywhere w-32">
            {row?.original?.eventName}
          </span>
        )
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => {
        const attendee = row.original
        return (
          <span className="font-semibold text-sm text-ss-neutral-600 leading-4.5  w-32 wrap-anywhere">
            {attendee.email}
          </span>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row?.original?.status
        return (
          <div className="w-24  wrap-anywhere">
            <Badge
              className={cn(
                'px-2 py-1 text-xs font-medium rounded-full',
                paymentStatusClasses(status)
              )}
              aria-label={`Status: ${status}`}
            >
              {status}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Ski Experience',
      cell: ({ row }) => {
        return (
          <span className="font-medium text-sm text-ss-neutral-600 leading-4.5  w-36  wrap-anywhere">
            {row?.original?.experience}
          </span>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Persona',
      cell: ({ row }) => {
        return (
          <span className="font-medium text-sm text-ss-neutral-600 leading-4.5  w-36  wrap-anywhere">
            {row?.original?.persona}
          </span>
        )
      },
    },
    {
      accessorKey: 'consentForm',
      header: 'Consent Form',
      cell: ({ row }) => {
        const isSigned = row?.original?.isSignedConsentForm
        return (
          <span
            className={cn(
              'text-xs font-medium rounded-full leading-4 px-2 py-0.5',
              isSigned
                ? 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100'
                : 'bg-ss-critical-50 text-ss-critical-200 border-ss-critical-100'
            )}
          >
            {isSigned ? 'Yes' : 'No'}
          </span>
        )
      },
    },
    {
      accessorKey: 'consentFormSignedAt',
      header: 'Consent Form Signed At',
      cell: ({ row }) => {
        return (
          <span className="font-medium text-sm text-ss-neutral-600 leading-4.5  w-36  wrap-anywhere">
            {row?.original?.isSignedConsentForm
              ? formatDateTimeIntl(row?.original?.consentFormSignedAt)
              : 'N/A'}
          </span>
        )
      },
    },
  ]

  if (isSuperAdmin) {
    baseColumns.push({
      id: 'actions',
      header: 'Download Consent Form',
      cell: ({ row }) => {
        const attendee = row.original
        const isConsentFormSigned = attendee?.isSignedConsentForm
        return (
          <div className="flex items-center justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="download consent form"
                  onClick={() => {
                    if (isConsentFormSigned) {
                      onDownloadConsentFormClick?.({
                        id: attendee?.id,
                        name: attendee?.name,
                        consentFormUrl:
                          attendee?.signedConsentFormUrl as string,
                      })
                    }
                  }}
                >
                  {attendeeId === attendee.id ? (
                    <Loader
                      className="size-5 animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Download className="size-5" aria-hidden="true" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isConsentFormSigned
                  ? `Download consent form`
                  : 'Consent form not signed'}
              </TooltipContent>
            </Tooltip>
          </div>
        )
      },
    })
  }

  return baseColumns
}
